"use client";

import { useState, FormEvent } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AnimatedBorderButton from "@/components/animated-border-button";

interface ContactFormProps {
  formServerUrl: string;
}

type FormStatus = "idle" | "submitting" | "success" | "error";

export default function ContactForm({ formServerUrl }: ContactFormProps) {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("submitting");
    setErrorMessage("");

    const form = e.currentTarget;
    const formData = new FormData();

    // Get form values
    const firstName = (form.elements.namedItem("firstname") as HTMLInputElement).value;
    const lastName = (form.elements.namedItem("lastname") as HTMLInputElement).value;
    const email = (form.elements.namedItem("email") as HTMLInputElement).value;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement).value;

    // Map to form server expected fields
    // firstName (hidden) = site identifier
    // lastName = full name from user
    // subject (hidden) = subject line
    // subjectPrefix = email (used for reply-to)
    // body = message content
    formData.append("firstName", "Huemer Website");
    formData.append("lastName", `${firstName} ${lastName}`);
    formData.append("subject", "Contact Form Submission");
    formData.append("subjectPrefix", email);
    formData.append("body", message);

    try {
      const response = await fetch(formServerUrl, {
        method: "POST",
        body: formData,
        redirect: "manual",
      });

      // A redirect (to success page) or 2xx means the email was sent
      if (response.ok || response.type === "opaqueredirect") {
        setStatus("success");
        form.reset();
      } else {
        const text = await response.text();
        setStatus("error");
        setErrorMessage(text || `Error: ${response.status}`);
      }
    } catch (err) {
      setStatus("error");
      setErrorMessage("Network error. Please try again.");
    }
  }

  if (status === "success") {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="mb-4 rounded-full bg-green-100 p-3 dark:bg-green-900">
          <svg
            className="h-8 w-8 text-green-600 dark:text-green-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h3 className="mb-2 text-xl font-semibold">Message Sent!</h3>
        <p className="text-muted-foreground">
          Thank you for reaching out. I'll get back to you soon.
        </p>
        <button
          onClick={() => setStatus("idle")}
          className="mt-4 text-sm underline hover:no-underline"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <div className="flex gap-10">
        <div className="flex-1 space-y-1.5">
          <Label htmlFor="firstname">First name</Label>
          <Input
            type="text"
            id="firstname"
            name="firstname"
            placeholder="Jane"
            required
            minLength={2}
            disabled={status === "submitting"}
          />
        </div>
        <div className="flex-1 space-y-1.5">
          <Label htmlFor="lastname">Last name</Label>
          <Input
            type="text"
            id="lastname"
            name="lastname"
            placeholder="Smith"
            required
            minLength={2}
            disabled={status === "submitting"}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="email">Email</Label>
        <Input
          type="email"
          id="email"
          name="email"
          placeholder="example@gmail.com"
          required
          disabled={status === "submitting"}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="message">Your message</Label>
        <Textarea
          placeholder="Type something..."
          id="message"
          name="message"
          className="min-h-[120px]"
          required
          minLength={7}
          disabled={status === "submitting"}
        />
      </div>

      {status === "error" && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
          {errorMessage || "Something went wrong. Please try again."}
        </div>
      )}

      <AnimatedBorderButton
        className="w-full"
        disabled={status === "submitting"}
      >
        {status === "submitting" ? "Sending..." : "Submit"}
      </AnimatedBorderButton>
    </form>
  );
}
